// Terraces and cliffs (docs/66 RM15) drawn into a country's painted ground in the kit's own colours
// (docs/164 ET7: crag in the highland, packed sand in the dunes, roots and earth in the forest). The shapes
// are `TileRenderer.drawTerraces`' own, square for square: a face of rounded rocks under a south-facing step
// (taller for two levels), a side under an east or west one, a lip on a north one, a cliff square of three
// courses; the colours come from one of the kit's pieces by OKLab. The heights the rules give stay as they are.
import type { TileData } from '../../../core/sim/types';
import type { Piece } from '../types';
import { heightOf } from '../../board/edges';
import type { Canvas, RGBA } from './canvas';
import { fromOklab, oklab } from './colour';
import { C, type Ctx } from './context';

/** Which of the kit's pieces a ledge is made of: the rock where a country is rocky, the earth under its ground where it is wooded. */
const SOURCE: Record<string, readonly string[]> = {
  // the crag itself where the country has one (its `rock` is the dark under the trees, not a crag)
  highland: ['grove_boulder_field', 'rock'], pinewood: ['grove_granite_outcrop', 'rock'], underdark: ['under', 'rock'], ashlands: ['rock', 'under'],
  dunes: ['floor_hardpan', 'under'],
  forest: ['under', 'rock'], autumn: ['under', 'rock'], winterwood: ['under', 'rock'], mirewood: ['under', 'rock'], jungle: ['under', 'rock'],
};

export interface LedgeColours {
  face: RGBA; light: RGBA; seam: RGBA; line: RGBA; shade: RGBA;
  /** The source piece's grain moved to the face's lightness (`w × h` RGBA), so a face is the kit's own rock or earth, not a flat fill. */
  grain?: { w: number; h: number; px: Uint8ClampedArray };
}

/** The ledge colours of a set: its source piece's mean in OKLab, a step lit, a step and two down. */
export function ledgeColours(ctx: Ctx): LedgeColours {
  const names = SOURCE[ctx.set.kit] ?? ['rock', 'under'];
  const piece = names.map((n) => ctx.all(n)[0]).find(Boolean) ?? ctx.all('floor')[0];
  let L = 0.45, a = 0.02, b = 0.03;
  if (piece) {
    let n = 0, sl = 0, sa = 0, sb = 0;
    for (let i = 0; i < piece.px.length; i += 4) {
      if (piece.px[i + 3]! < 255) continue;
      const [l, aa, bb] = oklab([piece.px[i]!, piece.px[i + 1]!, piece.px[i + 2]!, 255]);
      sl += l; sa += aa; sb += bb; n++;
    }
    if (n) { L = sl / n; a = sa / n; b = sb / n; }
  }
  // a face reads off the ground beside it: a step under the floor's own lightness and never far below it, so snow keeps
  // a pale crag and a dark country a face that isn't a hole
  const fl = meanL(ctx.all('floor')[0]) ?? 0.5;
  const f = Math.max(0.16, Math.min(fl - 0.04, Math.max(fl - 0.22, L)));
  let grain: LedgeColours['grain'];
  if (piece) {
    const px = new Uint8ClampedArray(piece.px.length);
    for (let i = 0; i < px.length; i += 4) {
      const [l, aa, bb] = piece.px[i + 3]! < 255 ? [L, a, b] : oklab([piece.px[i]!, piece.px[i + 1]!, piece.px[i + 2]!, 255]);
      // the grain kept at three quarters of its contrast, so a step reads as one face and not as the ground stood up
      px.set(fromOklab(f + (l - L) * 0.75, aa, bb), i);
    }
    grain = { w: piece.w, h: piece.h, px };
  }
  return {
    grain,
    face: fromOklab(f, a, b),
    light: fromOklab(Math.min(0.92, f + 0.12), a * 0.9, b * 0.9),
    seam: fromOklab(f - 0.1, a, b),
    line: fromOklab(Math.max(0.12, f * 0.42), a * 0.8, b * 0.8),
    shade: fromOklab(Math.max(0.1, f * 0.35), a * 0.6, b * 0.6),
  };
}

function meanL(p: Piece | undefined): number | undefined {
  if (!p) return undefined;
  let s = 0, n = 0;
  for (let i = 0; i < p.px.length; i += 4) if (p.px[i + 3] === 255) { s += oklab([p.px[i]!, p.px[i + 1]!, p.px[i + 2]!, 255])[0]; n++; }
  return n ? s / n : undefined;
}

const alpha = (c: RGBA, k: number): RGBA => [c[0], c[1], c[2], Math.round(255 * k)];

/** Draw every step and cliff of the board onto the ground (one art pixel a board pixel). */
export function drawLedges(ctx: Ctx, out: Canvas): void {
  const { sq } = ctx, { w, h } = sq;
  const tiles = sq.board.tiles;
  if (!tiles.some((t) => t && t.elev > 0)) return;
  const col = ledgeColours(ctx);
  const at = (x: number, y: number): TileData | undefined => (x < 0 || y < 0 || x >= w || y >= h ? undefined : tiles[y * w + x]);
  const cliff = (t: TileData | undefined): boolean => t?.terrain === 'Chasm' && t.elev > 0;
  const level = (t: TileData | undefined): number | undefined => (!t || t.terrain === 'Void' || (t.terrain === 'Chasm' && !cliff(t)) ? undefined : heightOf(t));
  const fill = (c: RGBA, x: number, y: number, rw: number, rh: number, k = 1): void => {
    if (rw <= 0 || rh <= 0) return;
    out.rect(x, y, x + rw - 1, y + rh - 1, k === 1 ? c : alpha(c, k));
  };
  /** The face's body: the source's grain where the kit has one, laid at board position so it runs on across squares. */
  const face = (x0: number, y0: number, rw: number, rh: number): void => {
    const g = col.grain;
    if (!g) { fill(col.face, x0, y0, rw, rh); return; }
    for (let v = 0; v < rh; v++) for (let u = 0; u < rw; u++) {
      const X = x0 + u, Y = y0 + v, i = (((Y % g.h) + g.h) % g.h * g.w + (((X % g.w) + g.w) % g.w)) * 4;
      out.set(X, Y, [g.px[i]!, g.px[i + 1]!, g.px[i + 2]!, 255]);
    }
  };
  /** One square's width of rounded rocks, `d` px tall from `top`: widths 5–9 px, some set a pixel lower. */
  const rocks = (ox: number, top: number, d: number, x: number, y: number, salt: string): void => {
    for (let k = 0, from = 0; from < C; k++) {
      const r = ctx.roll(`ledge ${salt}${k}`, x, y);
      let bw = 5 + (r % 5);
      if (C - (from + bw) < 4) bw = C - from;
      const bx = ox + from, t0 = top + ((r >>> 4) % 3 === 0 ? 1 : 0), rh = top + d - t0;
      const lower = Math.round(rh * 0.6);
      fill(col.face, bx + 1, t0, bw - 2, 1);
      face(bx, t0 + 1, bw, rh - 1);
      fill(col.light, bx + 2, t0, bw - 4, 1);
      fill(col.light, bx + 1, t0 + 1, 2, 1);
      fill(col.seam, bx, t0 + lower, bw, rh - lower, 0.55);
      fill(col.seam, bx + bw - 1, t0 + 2, 1, rh - 2);
      // a fleck of the rock's grain on the larger stones, seeded, so a run of rocks doesn't repeat
      if (bw >= 7 && (r >>> 9) % 2) fill(col.seam, bx + 2 + ((r >>> 11) % (bw - 4)), t0 + 2 + ((r >>> 14) % Math.max(1, lower - 2)), 1, 1);
      from += bw;
    }
  };
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const t = at(x, y), e = level(t);
    if (e === undefined) continue;
    const ox = x * C, oy = y * C;
    if (cliff(t)) {
      // a wall of rock: three courses on a dark bed, darker at its foot
      fill(col.seam, ox, oy, C, C);
      for (let r = 0; r < 3; r++) rocks(ox, oy + r * 8, 8, x, y, `cliff${r}.`);
      fill(col.shade, ox, oy + 16, C, 8, 0.3);
      fill(col.line, ox, oy + C - 1, C, 1);
      continue;
    }
    const higher = (dx: number, dy: number): number => {
      const ne = level(at(x + dx, y + dy));
      return ne === undefined || ne <= e ? 0 : Math.min(ne - e, 2);
    };
    const north = higher(0, -1), west = higher(-1, 0), east = higher(1, 0), south = higher(0, 1);
    if (north && cliff(at(x, y - 1))) {
      fill(col.shade, ox, oy, C, 2, 0.45);
    } else if (north) {
      // a face under the step, reaching up over the higher square's foot
      const d = north >= 2 ? 12 : 8, top = oy - 2;
      rocks(ox, top, d, x, y, 'face');
      fill(col.line, ox, top + d - 1, C, 1);
      fill(col.shade, ox, top + d, C, 2, 0.4);
    }
    for (const [rise, onLeft] of [[west, true], [east, false]] as const) {
      if (!rise) continue;
      if (rise < 2) {
        // a low side: a soft shade along the higher ground's edge, no hard line (a pale or dark rule reads as a selection, docs/168)
        const sx = onLeft ? ox : ox + C - 2;
        fill(col.line, onLeft ? sx : sx + 1, oy, 1, C, 0.45);
        fill(col.shade, onLeft ? sx + 1 : sx, oy, 1, C, 0.2);
        continue;
      }
      const d = 4, sx = onLeft ? ox : ox + C - d;
      face(sx, oy, d, C);
      fill(col.light, onLeft ? sx : sx + d - 1, oy, 1, C);
      const r = ctx.roll(onLeft ? 'ledge west' : 'ledge east', x, y);
      fill(col.seam, sx, oy + 5 + (r % 6), d, 1);
      fill(col.seam, sx, oy + 15 + ((r >>> 8) % 6), d, 1);
      fill(col.line, onLeft ? sx + d - 1 : sx, oy, 1, C);
    }
    if (south) {
      // a north-facing step shows only its lip
      const d = south >= 2 ? 3 : 2;
      fill(col.face, ox, oy + C - d, C, d, south >= 2 ? 1 : 0.6);
      fill(col.line, ox, oy + C - d, C, 1, south >= 2 ? 1 : 0.5);
    }
  }
}
